import React, { useEffect, useRef, useState } from "react";
import AuthHeader from "../../components/header/AuthHeader";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  EmailLessLogin,
  PhoneLessLogin,
  resendOTP,
} from "../../redux/actions/AuthUserActions";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import Loader from "../../components/loader/Loader";
import "../styles/flow-form.css";
import { spacePathFor } from "../../utils/roles";

function AuthOPT() {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, phone, method } = location.state || {};
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const inputs = useRef([]);
  const [resendTimer, setResendTimer] = useState(60);
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (value, index) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    if (digit && index < otp.length - 1) {
      inputs.current[index + 1].focus();
    }
  };

  // Pasting the whole code fills every box at once
  const handlePaste = (e) => {
    const digits = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, otp.length);

    if (!digits) return;
    e.preventDefault();

    const newOtp = otp.map((_, index) => digits[index] || "");
    setOtp(newOtp);
    inputs.current[Math.min(digits.length, otp.length - 1)].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputs.current[index - 1].focus();
    }
  };

  useEffect(() => {
    if (resendTimer > 0) {
      const interval = setInterval(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (otp.every((digit) => digit !== "")) {
      const otpCode = otp.join("");
      setSubmitting(true);
      if (method === "email") {
        dispatch(
          EmailLessLogin({ email, otp: Number(otpCode) }, handleCallback),
        );
      } else {
        dispatch(
          PhoneLessLogin({ phone, otp: Number(otpCode) }, handleCallback),
        );
      }
    }
  }, [otp]);

  const handleCallback = ({ success, message, user }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      navigate(spacePathFor(user.role));
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
      // Lets the user type the code again
      setOtp(["", "", "", "", ""]);
      inputs.current[0]?.focus();
    }
    setSubmitting(false);
  };

  const resendCode = () => {
    setSubmitting(true);
    dispatch(resendOTP({ email }, handleCodeCallback));
  };

  const handleCodeCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      Toast.fire({
        icon: "success",
        title: "Succès",
        text: message,
      });
      setResendTimer(60);
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }
    setSubmitting(false);
  };

  // The API can only send a new code by e-mail
  const canResend = method === "email";

  return (
    <div className="flow-form-page">
      <AuthHeader />
      <Loader isLoading={submitting} />

      <header className="flow-form-header">
        <h1 className="flow-form-title">
          Saisissez votre <span>code</span>
        </h1>
        <p className="flow-form-lead">
          {method === "phone" ? (
            <>
              Code à 5 chiffres envoyé par SMS
              {phone ? (
                <>
                  {" "}
                  au <strong>{phone}</strong>
                </>
              ) : null}
            </>
          ) : (
            <>
              Code à 5 chiffres envoyé par e-mail
              {email ? (
                <>
                  {" "}
                  à <strong>{email}</strong>
                </>
              ) : null}
            </>
          )}
          . Il est valable 30 minutes.
        </p>
      </header>

      <div className="flow-form">
        <div
          className="otp-inputs"
          role="group"
          aria-label="Code de vérification"
        >
          {otp.map((digit, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              className={`flow-input otp-input ${digit ? "is-filled" : ""}`}
              aria-label={`Chiffre ${index + 1}`}
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              ref={(el) => (inputs.current[index] = el)}
              autoFocus={index === 0}
            />
          ))}
        </div>

        <p className="otp-resend">
          {!canResend ? (
            <button type="button" onClick={() => navigate(-1)}>
              Recevoir un nouveau code
            </button>
          ) : resendTimer > 0 ? (
            <>Nouveau code disponible dans {resendTimer} s</>
          ) : (
            <button type="button" onClick={resendCode}>
              Recevoir un nouveau code
            </button>
          )}
        </p>

        <p className="flow-alt">
          <Link to="/login">Changer d'adresse ou de numéro</Link>
        </p>
      </div>
    </div>
  );
}

export default AuthOPT;
